import merge from 'lodash.merge';

class Modal {
  constructor(settings = {}) {
    settings = merge({
      title: '',
      content: '',
      classes: [],
      maxWidth: 800,
      showNav: false,
      navIndex: 0,
      navList: [],
      onClose: null,
    }, settings);

    this.settings = settings;
    this.modal = document.createElement('div');
    // modal--dialog: the overlay covers the screen and keeps the dialog
    // centered, scrolling inside it when it is taller (components/_modal.scss)
    this.modal.classList.add('modal', 'modal--dialog');

    this.currentIndex = this.settings.navIndex;
    this.navList = this.settings.navList;

    // keep one bound reference so the keyup listener can actually be removed
    // (binding inline creates a new function each time and leaks the listener)
    this.boundKeyUp = this.onKeyUpEvent.bind(this);
    this.isClosed = false;

    // add extra classes
    this.settings.classes.forEach(c => {
      this.modal.classList.add(c);
    });

    // build inner HTML
    this.modal.innerHTML = `
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-header__title">${settings.title}</h2>
          <button type="button" class="modal__close">
            <i class="fal fa-times"></i>
          </button>
        </div>
        <div class="modal__content"></div>
        ${this.settings.showNav ? `
          <button type="button" class="modal-nav-previous ${this.currentIndex > 0 ? 'is-active' : ''}"></button>
          <button type="button" class="modal-nav-next ${this.currentIndex + 1 < this.navList.length ? 'is-active' : ''}"></button>
        ` : ''}
      </div>
    `;

    this.modalContainer = this.modal.querySelector('.modal-content');
    this.title          = this.modal.querySelector('.modal-header__title');
    this.content        = this.modal.querySelector('.modal__content');
    this.closeButton    = this.modal.querySelector('.modal__close');

    if (settings.content) {
      this.setContent(settings.content);
    }

    if (settings.showNav && settings.navList.length) {
      this.prevButton = this.modal.querySelector('.modal-nav-previous');
      this.nextButton = this.modal.querySelector('.modal-nav-next');
      this.prevButton.addEventListener('click', this.previousItem.bind(this));
      this.nextButton.addEventListener('click', this.nextItem.bind(this));
    }

    this.closeButton.addEventListener('click', () => this.close());
    this.modal.addEventListener('mousedown', e => {
      if (e.target === this.modal) {
        this.close();
      }
    });

    document.addEventListener('keyup', this.boundKeyUp);
    document.body.appendChild(this.modal);
  }

  updateContentToNavItem() {
    if (this.navList[this.currentIndex]) {
      const item = this.navList[this.currentIndex];
      this.setTitle(item.title);
      this.setContent(item.content);
    }
  }

  previousItem() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.nextButton.classList.add('is-active');
      if (this.currentIndex === 0) {
        this.prevButton.classList.remove('is-active');
      }
      this.updateContentToNavItem();
    }
  }

  nextItem() {
    if (this.currentIndex + 1 < this.navList.length) {
      this.currentIndex++;
      this.prevButton.classList.add('is-active');
      if (this.currentIndex === this.navList.length - 1) {
        this.nextButton.classList.remove('is-active');
      }
      this.updateContentToNavItem();
    }
  }

  onKeyUpEvent(e) {
    // only the modal on show answers Escape: one hidden under another waits
    // its turn
    if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
      this.close();
    }
  }

  getContainer() {
    return this.modal;
  }

  setTitle(html) {
    this.title.innerHTML = html;
  }

  getContentContainer() {
    return this.modalContainer;
  }

  setContent(html) {
    this.content.innerHTML = html;
    if (window.lazyImages) {
      window.lazyImages.maybeProcessItems(this.content);
    }
    this.offsetContainer();
  }

  /**
   * Nothing to place by hand any more: the overlay is fixed to the screen and
   * centers the dialog itself, and a dialog that grows taller than the
   * screen (an editor opened, a list loaded) stops at its edge and scrolls
   * inside its content (.modal--dialog in components/_modal.scss). A
   * margin measured once, when it opened, used to push a dialog that grew
   * off the bottom of the screen. Kept for anything that still calls it.
   */
  offsetContainer() {
    this.modal.style.top = '';
    this.modalContainer.style.marginTop = '';
    this.modalContainer.style.maxHeight = '';
    this.modalContainer.style.overflowY = '';
  }

  open() {
    // remember scroll
    this.startPagePosition = window.scrollY;

    // hide any other open modal
    const currentOpen = document.querySelector('.modal.is-open');
    if (currentOpen && currentOpen !== this.modal) {
      this.previousModal = currentOpen;
      currentOpen.classList.remove('is-open');
    }

    this.modal.classList.add('is-open');

    if (this.settings.maxWidth) {
      this.modalContainer.style.maxWidth = `${this.settings.maxWidth}px`;
    }

    document.body.classList.add('has-modal');
    this.offsetContainer();
  }

  close() {
    // a second close (Escape after a button) has nothing left to do, and must
    // not run onClose or move the page again
    if (this.isClosed) {
      return;
    }

    this.isClosed = true;

    if (this.settings.onClose) {
      this.settings.onClose();
    }

    document.removeEventListener('keyup', this.boundKeyUp);

    if (document.body.contains(this.modal)) {
      document.body.removeChild(this.modal);
      document.body.classList.remove('has-modal');
    }

    // restore any previous modal (one that is still there to restore)
    if (this.previousModal && document.body.contains(this.previousModal)) {
      document.body.classList.add('has-modal');
      this.previousModal.classList.add('is-open');
    }

    // restore scroll
    window.scrollTo(0, this.startPagePosition);
  }
}

export default Modal;
